
import { db } from './firebase';
import { addDoc, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { AppNotification, Role } from '../types';
import { defaultTenantConfig } from '../config/tenant';
import { userNotificationsCollectionRef, usersCollectionRef } from './firestorePaths';

const logo = defaultTenantConfig.branding.logoUrl;

export const sendNotification = async (
  recipientId: string, 
  title: string, 
  message: string, 
  type: 'info' | 'alert' | 'success' = 'info',
  link?: string,
  tenantId?: string
) => {
  try {
    const notification: Partial<AppNotification> = {
      userId: recipientId,
      title,
      message,
      type,
      read: false,
      timestamp: new Date().toISOString(),
      link
    };

    // Write to Firestore subcollection: users/{userId}/notifications
    await addDoc(userNotificationsCollectionRef(db, recipientId, tenantId), notification);
  } catch (e) {
    console.error("Failed to send notification", e);
  }
};

export const notifyManagers = async (title: string, message: string, type: 'info' | 'alert' | 'success' = 'info', link?: string, tenantId?: string) => {
    try {
        // Find all managers and admins
        const constraints = tenantId
          ? [where('tenantId', '==', tenantId), where('role', 'in', [Role.Manager, Role.Admin])] as const
          : [where('role', 'in', [Role.Manager, Role.Admin])] as const;
        const q = query(usersCollectionRef(db, tenantId), ...constraints);
        const snap = await getDocs(q);
        
        const promises = snap.docs.map(doc => 
            sendNotification(doc.id, title, message, type, link, tenantId)
        );
        
        await Promise.all(promises);
    } catch (e) {
        console.error("Failed to notify managers", e);
    }
};

export const notifyAllStaff = async (title: string, message: string, type: 'info' | 'alert' | 'success' = 'info', link?: string, tenantId?: string) => {
    try {
        // Find all active staff
    const constraints = tenantId
      ? [where('tenantId', '==', tenantId), where('status', '==', 'Active')] as const
      : [where('status', '==', 'Active')] as const;
    const q = query(usersCollectionRef(db, tenantId), ...constraints);
        const snap = await getDocs(q);
        
        // Batching or limiting concurrent writes is ideal for large scale, 
        // but for this scope, parallel promises are acceptable.
        const promises = snap.docs.map(doc => 
      sendNotification(doc.id, title, message, type, link, tenantId)
        );
        
        await Promise.all(promises);
    } catch (e) {
        console.error("Failed to notify all staff", e);
    }
};

export const requestBrowserPermission = async () => {
  if (!('Notification' in window)) {
    console.log("This browser does not support desktop notification");
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  return false;
};

export const sendBrowserNotification = (title: string, body: string) => {
  if (!('Notification' in window)) return;

  if (Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: logo
    });
  }
};
